import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

async function geocode(place) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(place + ', South Africa')}&format=json&limit=1`,
      { headers: { 'Accept-Language': 'en' } }
    );
    const data = await res.json();
    if (!data.length) return null;
    return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
  } catch {
    return null;
  }
}

async function fetchRoute(from, to) {
  try {
    const res = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`
    );
    const data = await res.json();
    if (data.code !== 'Ok' || !data.routes.length) return null;
    return data.routes[0].geometry.coordinates.map(([lon, lat]) => [lat, lon]);
  } catch {
    return null;
  }
}

function FitBounds({ from, to }) {
  const map = useMap();
  useEffect(() => {
    if (from && to) {
      map.fitBounds([from, to], { padding: [50, 50] });
    }
  }, [from, to, map]);
  return null;
}

export default function TripMap({ pickup, delivery }) {
  const [fromCoord, setFromCoord] = useState(null);
  const [toCoord, setToCoord]     = useState(null);
  const [route, setRoute]         = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!pickup || !delivery) return;
    setLoading(true);
    setFromCoord(null);
    setToCoord(null);
    setRoute([]);

    Promise.all([geocode(pickup), geocode(delivery)]).then(([from, to]) => {
      setFromCoord(from);
      setToCoord(to);
      if (from && to) {
        fetchRoute(from, to).then(r => {
          setRoute(r || [from, to]);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });
  }, [pickup, delivery]);

  if (loading && !fromCoord) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f5f5f5', borderRadius: 12, fontSize: 14, color: '#6b7280' }}>
        Loading map...
      </div>
    );
  }

  const center = fromCoord || [-29.0, 25.0];

  return (
    <MapContainer center={center} zoom={6} style={{ height: '100%', width: '100%', borderRadius: 12 }}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {fromCoord && <Marker position={fromCoord} icon={startIcon} />}
      {toCoord   && <Marker position={toCoord}   icon={endIcon} />}
      {route.length > 1 && (
        <Polyline positions={route} pathOptions={{ color: '#9DC0DC', weight: 5, opacity: 0.85 }} />
      )}
      {fromCoord && toCoord && <FitBounds from={fromCoord} to={toCoord} />}
    </MapContainer>
  );
}
